import type {
  PdfEditorFieldBlockTemplate,
  PdfEditorFieldBlockTextAlignment,
  PdfEditorState,
} from '@urbanx/agx.ui-components2';
import { nswContractExchangeDocuSignAnchors } from './contractExchangeDocuSignAnchors';

const REFERENCE_PAGE_WIDTH = 945;
const REFERENCE_PAGE_HEIGHT = 1336;

const BLOCK_RECT = {
  x: 70,
  y: 770,
};

const SIGNATURE_BLOCK_RECT = {
  x: 65,
  y: 230,
};

const field = ({
  id,
  label,
  exportName,
  docusignAnchorText,
  textAlignment,
  x,
  y,
  width,
  height,
}: {
  id: string;
  label: string;
  exportName: string;
  docusignAnchorText?: string;
  textAlignment?: PdfEditorFieldBlockTextAlignment;
  x: number;
  y: number;
  width: number;
  height: number;
}) => ({
  id,
  label,
  exportName,
  ...(docusignAnchorText ? { docusignAnchorText } : {}),
  ...(textAlignment ? { textAlignment } : {}),
  x,
  y,
  width,
  height,
});

const makeInitialPlacement = (template: PdfEditorFieldBlockTemplate) => {
  const blockWidth = Math.max(...template.fields.map((field) => field.x + field.width));
  const blockHeight = Math.max(...template.fields.map((field) => field.y + field.height));

  return {
    pageIndex: template.initialPlacement.pageIndex,
    xPct: template.initialPlacement.x / template.initialPlacement.referencePageWidth,
    yPct: template.initialPlacement.y / template.initialPlacement.referencePageHeight,
    widthPct: blockWidth / template.initialPlacement.referencePageWidth,
    heightPct: blockHeight / template.initialPlacement.referencePageHeight,
  };
};

export const contractUploadFieldBlockTemplate: PdfEditorFieldBlockTemplate = {
  id: 'contract-upload-inputs',
  label: 'Contract Upload Inputs',
  buttonLabel: 'Add Contract Inputs',
  initialPlacement: {
    pageIndex: 0,
    x: BLOCK_RECT.x,
    y: BLOCK_RECT.y,
    referencePageWidth: REFERENCE_PAGE_WIDTH,
    referencePageHeight: REFERENCE_PAGE_HEIGHT,
  },
  fields: [
    field({
      id: 'buyer-1-name',
      label: 'Buyer 1',
      exportName: 'Buyer1Name',
      x: 85,
      y: 39,
      width: 200,
      height: 18,
    }),
    field({
      id: 'buyer-1-address',
      label: 'Buyer 1 Address',
      exportName: 'Buyer1Address',
      x: 85,
      y: 63,
      width: 220,
      height: 18,
    }),
    field({
      id: 'buyer-2-name',
      label: 'Buyer 2',
      exportName: 'Buyer2Name',
      x: 312,
      y: 39,
      width: 200,
      height: 18,
    }),
    field({
      id: 'buyer-2-address',
      label: 'Buyer 2 Address',
      exportName: 'Buyer2Address',
      x: 312,
      y: 63,
      width: 210,
      height: 18,
    }),
    field({
      id: 'buyer-3-name',
      label: 'Buyer 3',
      exportName: 'Buyer3Name',
      x: 531,
      y: 44,
      width: 160,
      height: 18,
    }),
    field({
      id: 'buyer-3-address',
      label: 'Buyer 3 Address',
      exportName: 'Buyer3Address',
      x: 531,
      y: 66,
      width: 220,
      height: 18,
    }),
    field({
      id: 'buyer-4-name',
      label: 'Buyer 4',
      exportName: 'Buyer4Name',
      x: 531,
      y: 0,
      width: 160,
      height: 18,
    }),
    field({
      id: 'buyer-4-address',
      label: 'Buyer 4 Address',
      exportName: 'Buyer4Address',
      x: 531,
      y: 21,
      width: 220,
      height: 18,
    }),
    field({
      id: 'purchaser-solicitor-name',
      label: "Purchaser's Solicitor",
      exportName: 'PurchaserSolicitorName',
      x: 162,
      y: 95,
      width: 260,
      height: 18,
    }),
    field({
      id: 'purchaser-solicitor-address',
      label: "Purchaser's Solicitor Address",
      exportName: 'PurchaserSolicitorAddress',
      x: 162,
      y: 116,
      width: 300,
      height: 18,
    }),
    field({
      id: 'purchaser-solicitor-phone',
      label: "Purchaser's Solicitor Phone",
      exportName: 'PurchaserSolicitorPhone',
      x: 542,
      y: 95,
      width: 260,
      height: 18,
    }),
    field({
      id: 'purchaser-solicitor-email',
      label: "Purchaser's Solicitor Email",
      exportName: 'PurchaserSolicitorEmail',
      x: 542,
      y: 117,
      width: 280,
      height: 18,
    }),
    field({
      id: 'price',
      label: 'Price',
      exportName: 'Price',
      textAlignment: 'right',
      x: 164,
      y: 141,
      width: 130,
      height: 18,
    }),
    field({
      id: 'deposit',
      label: 'Deposit',
      exportName: 'Deposit',
      textAlignment: 'right',
      x: 177,
      y: 164,
      width: 117,
      height: 18,
    }),
    field({
      id: 'balance',
      label: 'Balance',
      exportName: 'Balance',
      textAlignment: 'right',
      x: 164,
      y: 189,
      width: 130,
      height: 18,
    }),
    field({
      id: 'buyer-agent-name',
      label: "Buyer's Agent",
      exportName: 'BuyerAgentName',
      x: 100,
      y: 332,
      width: 260,
      height: 18,
    }),
    field({
      id: 'buyer-agent-address',
      label: "Buyer's Agent Address",
      exportName: 'BuyerAgentAddress',
      x: 100,
      y: 354,
      width: 310,
      height: 18,
    }),
    field({
      id: 'buyer-agent-phone',
      label: "Buyer's Agent Phone",
      exportName: 'BuyerAgentPhone',
      x: 100,
      y: 377,
      width: 150,
      height: 18,
    }),
    field({
      id: 'buyer-agent-email',
      label: "Buyer's Agent Email",
      exportName: 'BuyerAgentEmail',
      x: 100,
      y: 397,
      width: 240,
      height: 18,
    }),
  ],
};

export const contractUploadSignatureFieldBlockTemplate: PdfEditorFieldBlockTemplate = {
  id: 'contract-upload-signatures',
  label: 'Contract Upload Signatures',
  buttonLabel: 'Add Contract Signatures',
  initialPlacement: {
    pageIndex: 1,
    x: SIGNATURE_BLOCK_RECT.x,
    y: SIGNATURE_BLOCK_RECT.y,
    referencePageWidth: REFERENCE_PAGE_WIDTH,
    referencePageHeight: REFERENCE_PAGE_HEIGHT,
  },
  fields: [
    field({
      id: 'seller-1-signature',
      label: 'Vendor 1 Signature',
      exportName: 'Sellers1SignatureSignHere',
      docusignAnchorText: nswContractExchangeDocuSignAnchors.sellerSignature(1),
      x: 0,
      y: 0,
      width: 160,
      height: 22,
    }),
    field({
      id: 'seller-2-signature',
      label: 'Vendor 2 Signature',
      exportName: 'Sellers2SignatureSignHere',
      docusignAnchorText: nswContractExchangeDocuSignAnchors.sellerSignature(2),
      x: 0,
      y: 108,
      width: 160,
      height: 22,
    }),
    field({
      id: 'seller-3-signature',
      label: 'Vendor 3 Signature',
      exportName: 'Sellers3SignatureSignHere',
      docusignAnchorText: nswContractExchangeDocuSignAnchors.sellerSignature(3),
      x: 195,
      y: 0,
      width: 160,
      height: 22,
    }),
    field({
      id: 'seller-4-signature',
      label: 'Vendor 4 Signature',
      exportName: 'Sellers4SignatureSignHere',
      docusignAnchorText: nswContractExchangeDocuSignAnchors.sellerSignature(4),
      x: 195,
      y: 108,
      width: 160,
      height: 22,
    }),
    field({
      id: 'buyer-1-signature',
      label: 'Buyer 1 Signature',
      exportName: 'Buyers1SignatureSignHere',
      docusignAnchorText: nswContractExchangeDocuSignAnchors.buyerSignature(1),
      x: 417,
      y: 0,
      width: 160,
      height: 22,
    }),
    field({
      id: 'buyer-2-signature',
      label: 'Buyer 2 Signature',
      exportName: 'Buyers2SignatureSignHere',
      docusignAnchorText: nswContractExchangeDocuSignAnchors.buyerSignature(2),
      x: 417,
      y: 108,
      width: 160,
      height: 22,
    }),
    field({
      id: 'buyer-3-signature',
      label: 'Buyer 3 Signature',
      exportName: 'Buyers3SignatureSignHere',
      docusignAnchorText: nswContractExchangeDocuSignAnchors.buyerSignature(3),
      x: 612,
      y: 0,
      width: 160,
      height: 22,
    }),
    field({
      id: 'buyer-4-signature',
      label: 'Buyer 4 Signature',
      exportName: 'Buyers4SignatureSignHere',
      docusignAnchorText: nswContractExchangeDocuSignAnchors.buyerSignature(4),
      x: 612,
      y: 108,
      width: 160,
      height: 22,
    }),
    field({
      id: 'seller-company-1-signature',
      label: 'Vendor Company 1 Signature',
      exportName: 'Sellers5SignatureSignHere',
      docusignAnchorText: nswContractExchangeDocuSignAnchors.sellerSignature(5),
      x: 0,
      y: 478,
      width: 180,
      height: 22,
    }),
    field({
      id: 'seller-company-2-signature',
      label: 'Vendor Company 2 Signature',
      exportName: 'Sellers6SignatureSignHere',
      docusignAnchorText: nswContractExchangeDocuSignAnchors.sellerSignature(6),
      x: 210,
      y: 478,
      width: 180,
      height: 22,
    }),
    field({
      id: 'seller-company-1-name',
      label: 'Vendor Company 1 Name',
      exportName: 'Sellers5LegalName',
      x: 0,
      y: 545,
      width: 185,
      height: 18,
    }),
    field({
      id: 'seller-company-2-name',
      label: 'Vendor Company 2 Name',
      exportName: 'Sellers6LegalName',
      x: 210,
      y: 545,
      width: 185,
      height: 18,
    }),
    field({
      id: 'buyer-company-1-signature',
      label: 'Buyer Company 1 Signature',
      exportName: 'Buyers5SignatureSignHere',
      docusignAnchorText: nswContractExchangeDocuSignAnchors.buyerSignature(5),
      x: 417,
      y: 478,
      width: 180,
      height: 22,
    }),
    field({
      id: 'buyer-company-2-signature',
      label: 'Buyer Company 2 Signature',
      exportName: 'Buyers6SignatureSignHere',
      docusignAnchorText: nswContractExchangeDocuSignAnchors.buyerSignature(6),
      x: 627,
      y: 478,
      width: 180,
      height: 22,
    }),
    field({
      id: 'buyer-company-1-name',
      label: 'Buyer Company 1 Name',
      exportName: 'Buyers5NameFullName',
      x: 417,
      y: 545,
      width: 185,
      height: 18,
    }),
    field({
      id: 'buyer-company-2-name',
      label: 'Buyer Company 2 Name',
      exportName: 'Buyers6NameFullName',
      x: 627,
      y: 545,
      width: 185,
      height: 18,
    }),
  ],
};

export const contractUploadFieldBlockTemplates = [
  contractUploadFieldBlockTemplate,
  contractUploadSignatureFieldBlockTemplate,
];

export const initialContractUploadEditState: PdfEditorState = {
  version: 1,
  textBoxes: [],
  strikeOuts: [],
  fieldBlocks: [
    {
      id: 'contract-upload-inputs-block',
      templateId: contractUploadFieldBlockTemplate.id,
      deleteDisabled: true,
      ...makeInitialPlacement(contractUploadFieldBlockTemplate),
    },
    {
      id: 'contract-upload-signatures-block',
      templateId: contractUploadSignatureFieldBlockTemplate.id,
      deleteDisabled: true,
      ...makeInitialPlacement(contractUploadSignatureFieldBlockTemplate),
    },
  ],
};
